// @givmo/board — schedule or reschedule a meeting.
//
// The date and time are entered as the WALL CLOCK IN THE MEETING'S ZONE and converted on the way out
// (`zoned-time.ts`), because that is what the officer means and what the backend counts notice
// against. The zone is a field, not an assumption.
//
// Two things the officer should know before saving, and the dialog says both:
//   * Changing the date is a FRESH notice act. The backend re-stamps the notice anchor, bumps the
//     revision (so a calendar application updates the entry it already has), and re-resolves the
//     recorded period AS IT STANDS NOW.
//   * Changing only the zone is the SAME notice act — same parameter, same anchor, count recomputed.
//     A correction can legitimately move the figure by a day, and it is honest to say so rather than
//     let a number change with no explanation.
//
// CBG-FIX29: the date sits on its own row and the two times side by side, so a filled-in value
// shows whole (three columns at the dialog's 412px clipped "11/10/2026" to "11/10/20" and "06:00 PM"
// to "06:00 P"). And on a window shorter than the dialog, the title and the buttons stay in view
// while the fields scroll between them (the orchestrator's decision, 2026-10-05): the act never
// leaves the screen.
//
// CBG-CAL1 slice 1d: scheduling can send the invitations too. The Invitations section names the
// sender Board resolved (`features/calendar/invitations-section.tsx`); the button then reads
// "Schedule and send invitations", and saving is two calls — `create_meeting` (which sends nothing,
// as ever), then the officer's opt-in, `POST …/invitations`. If the second fails the meeting stands:
// the dialog closes as scheduled, the backend's sentence says why, and the meeting's page offers
// "Send invitations" (contract §2). An edit sends through the meeting's own calendar on its own, so
// the section is a new meeting's only. Dark until served: no senders read, no section.
//
// CBG-FIX60: the title box holds what the meeting's minutes can hold (`MEETING_TITLE_MAX_LENGTH`), so
// nobody meets the server's 422; a meeting kept from before that bound opens with the title's error
// and saves once it is shortened.
//
// CBG-YR6-MEET (frame c4): "Who is meeting" — the board, or one of its committees that stands. It is
// fixed when the meeting is scheduled (the backend refuses a change), so a new meeting's choice only,
// and offered only once the board has a committee. A committee meeting invites the committee's
// members and sends them its packet, which the note under the choice names; it is regular or special
// (an annual meeting is the board's), and its notice is counted against the committee's own period.
// Opened from a committee's page, the dialog starts on that committee. A committee chosen is no
// answer until it is read as one that stands, so Save waits while the list is read, when it couldn't
// be read (its line and Try again), and when the chosen committee has been dissolved since (the
// officer chooses again): a committee's meeting never goes out as the board's.

import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { isGivmoError, type OrgBoardMeetingOut, type BoardMeetingType } from '@givmo/api-client';
import { Button, Dialog, ErrorMessage, Select, TextField, toast } from '@givmo/design-system';
import { hasBoardPermission, useBoardHost } from '../../board-host';
import { keepScheduleDraft, takeScheduleDraft } from '../../calendar/calendar-connect';
import {
  useCalendarSenders,
  useOrgConnections,
  useRefreshCalendar,
} from '../../calendar/calendar-queries';
import { calendarWords as w } from '../../calendar/calendar-words';
import { useCalendarConnect } from '../../calendar/use-calendar-connect';
import { failureLine } from '../calendar/account-dialogs';
import { CALENDAR_MANAGE_PERMISSION } from '../calendar/board-calendar-card';
import { organizationDomain } from '../calendar/calendar-parts';
import { ConnectDialog } from '../calendar/connect-dialog';
import {
  InvitationsSection,
  defaultChoice,
  offersInvitations,
  sendsFromGoogle,
  type SendChoice,
} from '../calendar/invitations-section';
import { FieldNote } from '../../components/field-note';
import {
  availableTimeZones,
  browserTimeZone,
  instantToZonedFields,
  zonedFieldsToInstant,
} from './zoned-time';
import { useBoardOperations } from '../../board-runtime';
import { committeeMeetingsWords as cm } from '../committees/committee-meetings-words';
import { committeeInvitees, extractCommittees } from '../committees/committees-decode';

/**
 * The longest title a meeting takes: the backend's `MEETING_TITLE_MAX_LENGTH` (CBG-FIX60), a record's
 * title (300) less the longest default its minutes put before it ("Executive session — "). A browser
 * counts UTF-16 units where the server counts code points, so a box at the server's number is never
 * looser than the server. A meeting kept from before the bound can hold up to 300.
 */
export const MEETING_TITLE_MAX_LENGTH = 280;

// Approved (CBG-FIX60, 2026-10-14 15:33 PDT): the title's own error.
const TITLE_TOO_LONG = `A title can be up to ${MEETING_TITLE_MAX_LENGTH} characters.`;

const MEETING_TYPE_OPTIONS: readonly { value: BoardMeetingType; label: string }[] = [
  { value: 'regular', label: 'Regular' },
  { value: 'annual', label: 'Annual' },
  { value: 'special', label: 'Special' },
];

export interface MeetingFormDialogProps {
  orgRef: string;
  /** `null` to schedule a new meeting; a meeting to reschedule/edit it. */
  meeting: OrgBoardMeetingOut | null;
  /** CBG-YR6-MEET: a new meeting opened from a committee's page starts as that committee's. */
  initialCommitteeId?: string | null;
  onClose: () => void;
  onSaved: () => void;
}

export function MeetingFormDialog({
  orgRef,
  meeting,
  initialCommitteeId = null,
  onClose,
  onSaved,
}: MeetingFormDialogProps) {
  const operations = useBoardOperations();
  const { permissions, userId, userEmail, orgName } = useBoardHost();
  const isEdit = meeting !== null;
  // A new meeting's fields come back from this tab's keep after a full-page trip to Google (no
  // pop-up), so connecting a calendar mid-way never costs the officer what they typed.
  const [draft] = useState(() => (isEdit ? null : takeScheduleDraft(orgRef)));
  const initialZone = meeting?.time_zone ?? (draft?.timeZone || browserTimeZone());
  const initialStart = instantToZonedFields(meeting?.starts_at, initialZone);
  const initialEnd = instantToZonedFields(meeting?.ends_at, initialZone);

  // CBG-YR6-MEET: who is meeting — a committee's id, or '' for the board (a new meeting only).
  const [body, setBody] = useState<string>(() =>
    isEdit ? '' : draft?.committeeId || initialCommitteeId || '',
  );
  const committeesQuery = useQuery({
    ...operations.officerListBoardCommitteesOptions({ path: { org_ref: orgRef } }),
    enabled: !isEdit,
  });
  const allCommittees = extractCommittees(committeesQuery.data);
  const committees = allCommittees.filter((row) => row.status === 'active');
  const committee = committees.find((row) => row.id === body) ?? null;
  const committeeQuery = useQuery({
    ...operations.officerGetBoardCommitteeOptions({
      path: { org_ref: orgRef, committee_id: committee?.id ?? '' },
    }),
    enabled: committee !== null,
  });
  const invitees =
    committee !== null && committeeQuery.data ? committeeInvitees(committeeQuery.data) : null;
  // A committee chosen and not yet read as one that stands is no answer yet: Save waits.
  const bodyUnsettled = !isEdit && body !== '' && committee === null;
  // The list couldn't be read: no committee can be chosen, so the dialog says so and reads it again.
  const committeesUnread = !isEdit && committeesQuery.isError;
  // The list is read and the chosen committee isn't among those that stand (a draft kept across a
  // calendar sign-in, its committee dissolved since): the officer chooses again.
  const dissolvedBody = bodyUnsettled && committeesQuery.isSuccess;
  const dissolvedName = allCommittees.find((row) => row.id === body)?.name ?? null;

  const [title, setTitle] = useState(meeting?.title ?? draft?.title ?? '');
  const [meetingType, setMeetingType] = useState<BoardMeetingType>(
    (meeting?.meeting_type as BoardMeetingType) ??
      ((draft?.meetingType || 'regular') as BoardMeetingType),
  );
  const [timeZone, setTimeZone] = useState(initialZone);
  const [startDate, setStartDate] = useState(initialStart?.date ?? draft?.startDate ?? '');
  const [startTime, setStartTime] = useState(initialStart?.time ?? draft?.startTime ?? '');
  const [endTime, setEndTime] = useState(initialEnd?.time ?? draft?.endTime ?? '');
  const [location, setLocation] = useState(meeting?.location ?? draft?.location ?? '');
  const [videoLink, setVideoLink] = useState(meeting?.video_link ?? draft?.videoLink ?? '');
  const [error, setError] = useState<string | null>(null);

  // ── The Invitations section (a new meeting's only) ───────────────────────────────────────────
  const senders = useCalendarSenders(orgRef, !isEdit);
  const offered = !isEdit && senders.data !== undefined && offersInvitations(senders.data);
  const ownConnections = useOrgConnections(orgRef, offered);
  const ownConnection =
    userId === null || userId === undefined
      ? undefined
      : (ownConnections.data ?? []).find(
          (row) =>
            row.account_kind === 'personal' &&
            row.owner_user_id === userId &&
            row.status !== 'disconnected',
        );
  const [choice, setChoice] = useState<SendChoice | null>(null);
  const sendFrom: SendChoice = choice ?? defaultChoice(senders.data);
  const sends = offered && sendFrom !== 'none';
  const refreshCalendar = useRefreshCalendar(orgRef);
  const [connecting, setConnecting] = useState(false);
  const connect = useCalendarConnect(orgRef, () => setChoice('my_calendar'));
  const canManage = hasBoardPermission(permissions ?? null, CALENDAR_MANAGE_PERMISSION);
  const keepDraft = () =>
    keepScheduleDraft({
      orgRef,
      committeeId: committee?.id ?? '',
      title,
      meetingType,
      timeZone,
      startDate,
      startTime,
      endTime,
      location,
      videoLink,
    });

  const startsAt = zonedFieldsToInstant({ date: startDate, time: startTime }, timeZone);
  const endsAt =
    endTime.trim().length > 0
      ? zonedFieldsToInstant({ date: startDate, time: endTime }, timeZone)
      : null;

  const dateChanged = isEdit && startsAt !== null && startsAt !== meeting.starts_at;
  const zoneOnlyChanged = isEdit && !dateChanged && timeZone !== meeting.time_zone;

  const save = useMutation({
    mutationFn: async () => {
      if (startsAt === null) throw new Error('unusable-start');
      const shared = {
        title: title.trim(),
        starts_at: startsAt,
        time_zone: timeZone,
        ends_at: endsAt,
        location: location.trim().length > 0 ? location.trim() : null,
        video_link: videoLink.trim().length > 0 ? videoLink.trim() : null,
      };
      if (isEdit) {
        await operations.officerUpdateBoardMeeting({
          path: { org_ref: orgRef, meeting_id: meeting.id },
          body: shared,
          throwOnError: true,
        });
        return null;
      }
      const { data: created } = await operations.officerCreateBoardMeeting({
        path: { org_ref: orgRef },
        body: {
          ...shared,
          // An annual meeting is the board's: a committee's starts regular.
          meeting_type: committee !== null && meetingType === 'annual' ? 'regular' : meetingType,
          ...(committee !== null ? { committee_id: committee.id } : {}),
        },
        throwOnError: true,
      });
      if (!offered || sendFrom === 'none') return null;
      // The meeting stands whatever happens next: a failed Send is said, never a failed schedule.
      try {
        const sender = (senders.data?.senders ?? []).find((row) => row.send_from === sendFrom);
        await operations.officerSendBoardMeetingInvitations({
          path: { org_ref: orgRef, meeting_id: created.id },
          body: {
            send_from: sendFrom,
            ...(sender?.connection_id ? { connection_id: sender.connection_id } : {}),
          },
          throwOnError: true,
        });
        return null;
      } catch (sendFailure) {
        return failureLine(sendFailure);
      }
    },
    onSuccess: async (sendFailure) => {
      toast.success(isEdit ? 'Meeting updated.' : 'Meeting scheduled.');
      if (sendFailure !== null) toast.error(sendFailure);
      if (!isEdit) await refreshCalendar();
      onSaved();
    },
    onError: (caught) => {
      setError(
        isGivmoError(caught)
          ? caught.message
          : 'We couldn’t save this meeting. Please check the date and time and try again.',
      );
    },
  });

  // A meeting kept from before CBG-FIX60 may hold a longer title than the box takes now: it is the
  // title's error, and Save waits for it, rather than a 422 after the officer saves.
  const titleTooLong = title.trim().length > MEETING_TITLE_MAX_LENGTH;
  const canSave =
    title.trim().length > 0 &&
    !titleTooLong &&
    startsAt !== null &&
    !bodyUnsettled &&
    !save.isPending;

  return (
    <Dialog.Root open onOpenChange={(next) => !next && !save.isPending && onClose()}>
      {/* The card is clamped to the window (the design system's default); the title and the buttons
          stay put and only the fields between them scroll, so the act never leaves the screen on a
          short window (CBG-FIX29). The fields' own gutter keeps a focus ring whole at the edge. */}
      <Dialog.Content
        aria-label={isEdit ? 'Edit this meeting' : 'Schedule a meeting'}
        className="flex flex-col gap-16 overflow-hidden"
        testId="meeting-form-dialog"
      >
        <Dialog.Title className="shrink-0 font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          {isEdit ? 'Edit this meeting' : 'Schedule a meeting'}
        </Dialog.Title>

        <div
          className="-mx-4 flex min-h-0 flex-1 flex-col gap-16 overflow-y-auto overscroll-contain px-4 py-2"
          data-testid="meeting-form-fields"
        >
          {committeesUnread ? (
            <div className="flex flex-col items-start gap-6" data-testid="meeting-body-unread">
              <ErrorMessage>{cm.committeesUnread}</ErrorMessage>
              <Button
                appearance="saas"
                tone="secondary"
                onClick={() => void committeesQuery.refetch()}
                testId="meeting-body-retry"
              >
                {w.tryAgain}
              </Button>
            </div>
          ) : null}
          {!isEdit && (committees.length > 0 || dissolvedBody) ? (
            <div className="flex flex-col gap-6">
              <Select
                label={cm.whoIsMeeting}
                appearance="saas"
                value={committee?.id ?? (body === '' ? 'board' : null)}
                onValueChange={(next) => {
                  const id = next === null || next === 'board' ? '' : next;
                  setBody(id);
                  if (id !== '' && meetingType === 'annual') setMeetingType('regular');
                }}
                options={[
                  { value: 'board', label: cm.theBoard },
                  ...committees.map((row) => ({ value: row.id, label: row.name })),
                ]}
                testId="meeting-body"
              />
              {dissolvedBody ? (
                <ErrorMessage testId="meeting-body-dissolved">
                  {cm.chosenCommitteeDissolved(dissolvedName)}
                </ErrorMessage>
              ) : null}
              {committee !== null && invitees !== null ? (
                <FieldNote testId="meeting-body-note">
                  {invitees.length > 0
                    ? cm.invitesMembers(committee.name, invitees)
                    : cm.invitesNobody(committee.name)}
                </FieldNote>
              ) : null}
            </div>
          ) : null}

          <TextField
            label="Title"
            value={title}
            maxLength={MEETING_TITLE_MAX_LENGTH}
            error={titleTooLong}
            errorMessage={titleTooLong ? TITLE_TOO_LONG : undefined}
            onChange={(event) => setTitle(event.target.value)}
            testId="meeting-title"
          />

          {!isEdit ? (
            <>
              <Select
                label="Kind of meeting"
                appearance="saas"
                value={meetingType}
                onValueChange={(next) => next !== null && setMeetingType(next as BoardMeetingType)}
                options={MEETING_TYPE_OPTIONS.filter(
                  // An annual meeting is the board's; a committee holds regular and special ones.
                  (option) => committee === null || option.value !== 'annual',
                ).map((option) => ({
                  value: option.value,
                  label: option.label,
                }))}
                testId="meeting-type"
              />
              <FieldNote>
                {committee !== null
                  ? cm.committeeNotice(committee.name)
                  : 'Your recorded notice period can differ by kind of meeting.'}
              </FieldNote>
            </>
          ) : null}

          <Select
            label="Time zone"
            appearance="saas"
            value={timeZone}
            onValueChange={(next) => next !== null && setTimeZone(next)}
            options={availableTimeZones().map((zone) => ({ value: zone, label: zone }))}
            testId="meeting-time-zone"
          />
          <FieldNote>
            The board’s own time zone. The date, the time, and the notice count are all read in this
            zone — not in yours.
          </FieldNote>

          {/* CBG-FIX29: the date on its own row and the two times side by side, so each value shows
            whole at the dialog's width (three columns clipped them). */}
          <TextField
            label="Date"
            type="date"
            value={startDate}
            onChange={(event) => setStartDate(event.target.value)}
            testId="meeting-start-date"
          />
          <div className="grid grid-cols-2 gap-12" data-testid="meeting-times">
            <TextField
              label="Starts"
              type="time"
              value={startTime}
              onChange={(event) => setStartTime(event.target.value)}
              testId="meeting-start-time"
            />
            <TextField
              label="Ends (optional)"
              type="time"
              value={endTime}
              onChange={(event) => setEndTime(event.target.value)}
              testId="meeting-end-time"
            />
          </div>

          <TextField
            label="Where (optional)"
            value={location}
            onChange={(event) => setLocation(event.target.value)}
            testId="meeting-location"
          />

          <TextField
            label="Video link (optional)"
            value={videoLink}
            onChange={(event) => setVideoLink(event.target.value)}
            testId="meeting-video-link"
          />
          <FieldNote>
            {/* With a Google sender, an empty link becomes the Meet link Google Calendar adds. */}
            {sends && sendsFromGoogle(senders.data, sendFrom)
              ? w.videoNoteGoogle
              : 'Paste a link from whatever service you use — Givmo doesn’t host the call.'}
          </FieldNote>

          {offered && senders.data ? (
            <InvitationsSection
              senders={senders.data}
              ownConnection={ownConnection}
              ownConnectionKnown={!ownConnections.isPending}
              orgName={orgName ?? ''}
              choice={sendFrom}
              onChoice={setChoice}
              onConnect={() => {
                if (ownConnection !== undefined && ownConnection.status !== 'connected') {
                  connect.begin(
                    {
                      accountKind: 'personal',
                      reconnectConnectionId: ownConnection.connection_id,
                      loginHint: ownConnection.account_email,
                    },
                    { beforeRedirect: keepDraft },
                  );
                  return;
                }
                connect.reset();
                setConnecting(true);
              }}
            />
          ) : null}

          {dateChanged ? (
            <p
              className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary"
              data-testid="meeting-reschedule-note"
            >
              Changing the date counts as giving notice again. The notice figures will be
              recalculated from today against the period you have recorded now, and everyone’s
              calendar entry will update rather than being duplicated.
            </p>
          ) : null}

          {zoneOnlyChanged ? (
            <p
              className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary"
              data-testid="meeting-zone-note"
            >
              Changing only the time zone doesn’t count as giving notice again, but the day count is
              read in this zone — so the figure may move by a day.
            </p>
          ) : null}

          {startDate.length > 0 && startTime.length > 0 && startsAt === null ? (
            <ErrorMessage testId="meeting-datetime-error">
              We couldn’t read that date and time. Please check them.
            </ErrorMessage>
          ) : null}

          {error !== null ? <ErrorMessage testId="meeting-form-error">{error}</ErrorMessage> : null}
        </div>

        <div className="flex shrink-0 justify-end gap-10">
          <Button
            appearance="saas"
            tone="secondary"
            disabled={save.isPending}
            onClick={onClose}
            testId="meeting-form-cancel"
          >
            Cancel
          </Button>
          <Button
            appearance="saas"
            tone="primary"
            loading={save.isPending}
            disabled={!canSave}
            onClick={() => {
              setError(null);
              save.mutate();
            }}
            testId="meeting-form-save"
          >
            {isEdit ? 'Save changes' : sends ? w.scheduleAndSend : 'Schedule it'}
          </Button>
        </div>
        {/* Nested inside the card, so the dialog stack is Base UI's own: the connect dialog takes
            focus over this one and hands it back. A reconnect goes straight to Google, and its
            answer opens here too. */}
        {connecting || connect.outcome !== null ? (
          <ConnectDialog
            mode="mine"
            canManage={canManage}
            boardAccountExists={(ownConnections.data ?? []).some(
              (row) => row.account_kind === 'board_account' && row.status !== 'disconnected',
            )}
            exampleDomain={organizationDomain([
              userEmail,
              ...(ownConnections.data ?? []).map((row) => row.account_email),
            ])}
            orgName={orgName ?? ''}
            flow={connect}
            beforeRedirect={keepDraft}
            onClose={() => {
              connect.reset();
              setConnecting(false);
            }}
          />
        ) : null}
      </Dialog.Content>
    </Dialog.Root>
  );
}
